import { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart3, TrendingUp, Activity } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ai-management`;

export default function UsageAnalytics() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ task_type: '', status: '' });

  useEffect(() => {
    fetchStats();
    fetchLogs();
  }, [filter]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/usage-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to fetch usage statistics');
    }
  };

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({ limit: 50 });
      if (filter.task_type) params.append('task_type', filter.task_type);
      if (filter.status) params.append('status', filter.status);
      
      const response = await axios.get(`${API}/usage-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg text-slate-600">Loading analytics...</div></div>;
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-800 mb-2">Usage Analytics</h1>
        <p className="text-slate-600">Monitor AI integration usage, costs, and performance</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-slate-600 text-sm">Total Jobs</div>
            <Activity className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-3xl font-bold text-slate-800">{stats.total_jobs}</div>
          <div className="text-xs text-green-600 mt-1">{stats.successful_jobs} successful</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-slate-600 text-sm">Success Rate</div>
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-slate-800">
            {stats.total_jobs > 0 ? ((stats.successful_jobs / stats.total_jobs) * 100).toFixed(1) : 0}%
          </div>
          <div className="text-xs text-red-600 mt-1">{stats.failed_jobs} failed</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-slate-600 text-sm">Total Credits Used</div>
            <BarChart3 className="w-5 h-5 text-purple-600" />
          </div>
          <div className="text-3xl font-bold text-slate-800">{stats.total_credits_used.toLocaleString()}</div>
          <div className="text-xs text-slate-500 mt-1">₹{stats.total_cost.toFixed(2)} cost</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-slate-600 text-sm">Avg Execution Time</div>
            <Activity className="w-5 h-5 text-orange-600" />
          </div>
          <div className="text-3xl font-bold text-slate-800">{stats.avg_execution_time_ms.toFixed(0)}ms</div>
        </div>
      </div>

      {/* Jobs by Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="font-semibold text-lg text-slate-800 mb-4">Jobs by Task Type</h3>
          <div className="space-y-3">
            {Object.entries(stats.jobs_by_task_type).map(([type, count]) => (
              <div key={type} className="flex justify-between items-center">
                <span className="text-sm text-slate-700">{type.replace('_', ' ')}</span>
                <div className="flex items-center">
                  <div className="w-32 bg-slate-100 rounded-full h-2 mr-3">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${stats.total_jobs > 0 ? (count / stats.total_jobs) * 100 : 0}%` }}></div>
                  </div>
                  <span className="font-semibold text-slate-800 w-12 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="font-semibold text-lg text-slate-800 mb-4">Top Models</h3>
          <div className="space-y-3">
            {stats.top_models.map((model, idx) => (
              <div key={idx} className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-medium text-slate-800">{model.model}</div>
                  <div className="text-xs text-slate-500">{model.total_credits} credits</div>
                </div>
                <span className="font-semibold text-purple-600">{model.usage_count} uses</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Logs */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="font-semibold text-lg text-slate-800">Recent Jobs</h3>
          <div className="flex space-x-2">
            <select className="px-3 py-1 border rounded text-sm" value={filter.status} onChange={(e) => setFilter({...filter, status: e.target.value})}>
              <option value="">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700">Job ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700">Task</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700">Model</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700">Credits</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700">Time</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {logs.slice(0, 20).map((log) => (
                <tr key={log.job_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-xs font-mono text-slate-600">{log.job_id.substring(0, 8)}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{log.user_email || log.user_id.substring(0, 8)}</td>
                  <td className="px-4 py-3 text-xs"><span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">{log.task_type}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-600">{log.model_name}</td>
                  <td className="px-4 py-3 text-xs font-semibold text-green-600">{log.credits_used}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{log.execution_time_ms}ms</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {log.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
